import React, { createContext, useContext, useEffect, useState } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('AuthContext initializing...');
    // Check if we're returning from Google OAuth
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const hash = window.location.hash;
    
    console.log('URL params:', { code, hash });
    
    if (hash && hash.includes('id_token')) {
      // Implicit flow - token in hash
      console.log('Handling implicit flow callback');
      handleGoogleCallback(null);
    } else if (code) {
      // Authorization code flow
      console.log('Handling authorization code flow');
      handleGoogleCallback(code);
    } else {
      // Check for existing user session
      const savedUser = localStorage.getItem('user');
      console.log('Checking saved user:', savedUser ? 'found' : 'not found');
      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }
      setLoading(false);
    }
  }, []);

  const handleGoogleCallback = async (code) => {
    try {
      // Parse the ID token from URL hash (implicit flow)
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const idToken = params.get('id_token');
      
      if (idToken) {
        // Decode the JWT token to get user info
        const payload = JSON.parse(atob(idToken.split('.')[1]));
        const userData = {
          id: payload.sub,
          email: payload.email,
          name: payload.name,
          picture: payload.picture,
          loginTime: new Date().toISOString()
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        // For authorization code flow, we'd need a backend
        // For now, show a message
        console.log('Authorization code received:', code);
        console.log('Note: Full OAuth flow requires a backend to exchange code for tokens');
        
        // Temporary: Use placeholder data
        const userData = {
          id: 'google_' + Date.now(),
          email: 'user@example.com',
          name: 'Google User',
          picture: null,
          loginTime: new Date().toISOString()
        };
        
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Google callback error:', error);
    } finally {
      // Always set loading to false
      setLoading(false);
    }
  };

  const login = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '919810165511-chjm11tua37m8na8bfvsbufi1t828hlm.apps.googleusercontent.com';
    const redirectUri = window.location.origin + window.location.pathname;
    const scope = 'openid email profile';
    
    // Use implicit flow to get user data directly (response_type=token id_token)
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=${encodeURIComponent(scope)}&` +
      `response_type=token id_token&` +
      `nonce=${Math.random().toString(36).substring(7)}`;
    
    window.location.href = googleAuthUrl;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const value = {
    user,
    login,
    logout,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};